import { ChangeEvent, ReactElement, useState } from "react";
import WindowedModalV2 from "designer/modals-v2/windowed-modal/windowed-modal";
import { getAppFacilities } from "webapp/lib/app-renderer";
import { GlideDropdown } from "designer/ui/smart/glide-dropdown/glide-dropdown";
import { Button } from "designer/ui/smart/button/button-style";
import { styled } from "sharedUI/styles";
import HighlightTextArea from "sharedUI/ui/smart/highlight-textarea/highlight-textarea";
import { trackEvent } from "analytics";

type ChurnSurveySubmitCallback = () => Promise<void> | void;

interface ChurnSurveyParams {
    appID: string;
    reason: string;
    explanation?: string;
}

async function storeChurnSurvey(params: ChurnSurveyParams): Promise<void> {
    await getAppFacilities().callAuthCloudFunction("storeChurnSurvey", params);
}

export const ChurnSurveyStyle = styled.div`
    margin: 48px 0;
    width: 380px;

    h2 {
        font-size: 26px;
        font-weight: 900;
    }

    .label {
        font-size: 14px;
        font-weight: 600;
        margin-top: 24px;
    }
`;

interface ChurnSurveyReasonWithoutExplanation {
    text: string;
    needsExplanation: false;
    explanationRequired: false;
}

interface ChurnSurveyReasonWithExplanation {
    text: string;
    needsExplanation: true;
    explanationRequired: boolean;
    explanationLabel: string;
    explanationPlaceholder: string;
}

type ChurnSurveyReason = ChurnSurveyReasonWithoutExplanation | ChurnSurveyReasonWithExplanation;

function reasonNeedsExplanation(reason: ChurnSurveyReason | undefined): reason is ChurnSurveyReasonWithExplanation {
    return reason?.needsExplanation ?? false;
}

const CHURN_SURVEY_REASONS: ChurnSurveyReason[] = [
    { text: "Upgrading a different app", needsExplanation: false, explanationRequired: false },
    { text: "Too expensive", needsExplanation: false, explanationRequired: false },
    {
        text: "I found Glide too hard to learn",
        needsExplanation: true,
        explanationRequired: false,
        explanationLabel: "Please explain",
        explanationPlaceholder: "Optional...",
    },
    {
        text: "Glide is missing features I need",
        needsExplanation: true,
        explanationRequired: true,
        explanationLabel: "Which features are missing?",
        explanationPlaceholder: "Please explain...",
    },
    {
        text: "App project failed",
        needsExplanation: true,
        explanationRequired: false,
        explanationLabel: "Please explain",
        explanationPlaceholder: "Optional...",
    },
    {
        text: "Other",
        needsExplanation: true,
        explanationRequired: true,
        explanationLabel: "Please explain",
        explanationPlaceholder: "",
    },
];

interface ChurnSurveyModalProps {
    appID: string;
    onClose(): void;
    onSubmit: ChurnSurveySubmitCallback;
}

export function ChurnSurveyModal(props: ChurnSurveyModalProps): ReactElement {
    const { appID, onClose, onSubmit } = props;

    const [selectedReason, setSelectedReason] = useState<ChurnSurveyReason | undefined>(undefined);
    const [explanation, setExplanation] = useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState<boolean>();

    const isExplanationInputRequired = selectedReason?.explanationRequired ?? false;

    const isButtonDisabled =
        selectedReason === undefined || (isExplanationInputRequired && explanation.trim() === "") || isSubmitting;

    const submit = async () => {
        if (selectedReason === undefined) {
            return;
        }

        setIsSubmitting(true);

        const maybeExplanation = explanation === "" ? undefined : explanation;

        await storeChurnSurvey({
            appID,
            reason: selectedReason.text,
            explanation: maybeExplanation,
        });

        trackEvent("downgrade", { app_id: appID, reason: selectedReason.text, explanation });

        await onSubmit();

        onClose();
    };

    const onExplanationChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
        setExplanation(e.target.value);
    };

    return (
        <WindowedModalV2 canClose={true} onClose={onClose} padding={48}>
            <ChurnSurveyStyle>
                <h2>We’re sorry this didn’t work out as planned.</h2>

                <label>
                    <p className="label">Please tell us why you’re downgrading</p>
                    <GlideDropdown<ChurnSurveyReason>
                        items={CHURN_SURVEY_REASONS}
                        selected={selectedReason}
                        descriptionForItem={i => i.text}
                        defaultDisplayLabel="Choose one answer..."
                        onItemSelect={setSelectedReason}
                        tallerVariation={true}
                    />
                </label>

                {reasonNeedsExplanation(selectedReason) && (
                    <>
                        <label>
                            <p className="label">{selectedReason.explanationLabel}</p>
                            <HighlightTextArea
                                value={explanation}
                                onChange={onExplanationChange}
                                placeholder={selectedReason.explanationPlaceholder}
                                minHeight={56}
                                alignTop={true}
                            />
                        </label>
                    </>
                )}

                <Button
                    variant="destroy"
                    fullSize={true}
                    tallerVariation={true}
                    mt={24}
                    disabled={isButtonDisabled}
                    onClick={submit}>
                    Cancel Plan
                </Button>
            </ChurnSurveyStyle>
        </WindowedModalV2>
    );
}
